import React, { useState, useEffect, useCallback } from 'react';
import { inboxApi } from './api/inbox';
import { permissionsApi } from '../../api/permissions';
import { formatIndianCurrency } from '../accounts/utils/accountUtils';
import StatsCard from './components/StatsCard';
import ApprovalCard from './components/ApprovalCard';
import DealDetailsModal from './DealDetailsModal';
import DuplicateDealsModal from './DuplicateDealsModal';

const Inbox = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [stats, setStats] = useState({
    total_pending: 0,
    total_approved: 0,
    total_rejected: 0
  });
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedDuplicateDeal, setSelectedDuplicateDeal] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    await Promise.all([
      fetchPermissions(),
      fetchPendingApprovals(),
      fetchStats()
    ]);
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchPermissions = async () => {
    try {
      const data = await permissionsApi.getUserPermissions();
      setPermissions(data.permissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions({});
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await inboxApi.getPendingApprovals();
      setPendingApprovals(data);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      setError(error.message);
      setPendingApprovals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setIsStatsLoading(true);
      const data = await inboxApi.getApprovalStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const handleApproveDeal = async (dealId) => {
    try {
      await inboxApi.approveDeal(dealId);
      
      // Remove the approved deal from the list
      setPendingApprovals(prev => prev.map(item => {
        if (item.type === 'DUPLICATE') {
          // Remove the specific new deal from the new_deals array
          const updatedNewDeals = item.new_deals?.filter(deal => deal.ID !== dealId) || [];
          // If no more new deals, remove the entire duplicate group
          if (updatedNewDeals.length === 0) {
            return null;
          }
          return { ...item, new_deals: updatedNewDeals };
        }
        // For KAM_APPROVAL deals, remove entirely if it matches
        return item.ID !== dealId ? item : null;
      }).filter(Boolean));
      
      // Update stats
      await fetchStats();
      
      // Show success message (you can implement a toast notification here)
    } catch (error) {
      console.error('Error approving deal:', error);
      // Show error message (you can implement a toast notification here)
      alert('Error approving deal: ' + error.message);
    }
  };

  const handleRejectDeal = async (dealId) => {
    try {
      await inboxApi.rejectDeal(dealId);
      
      // Remove the rejected deal from the list
      setPendingApprovals(prev => prev.map(item => {
        if (item.type === 'DUPLICATE') {
          // Remove the specific new deal from the new_deals array
          const updatedNewDeals = item.new_deals?.filter(deal => deal.ID !== dealId) || [];
          // If no more new deals, remove the entire duplicate group
          if (updatedNewDeals.length === 0) {
            return null;
          }
          return { ...item, new_deals: updatedNewDeals };
        }
        // For KAM_APPROVAL deals, remove entirely if it matches
        return item.ID !== dealId ? item : null;
      }).filter(Boolean));
      
      // Update stats
      await fetchStats();
      
      // Show success message (you can implement a toast notification here)
    } catch (error) {
      console.error('Error rejecting deal:', error);
      // Show error message (you can implement a toast notification here)
      alert('Error rejecting deal: ' + error.message);
    }
  };

  const handleViewDetails = (deal) => {
    setSelectedDeal(deal);
    setIsDetailsModalOpen(true);
  };

  const handleViewDuplicateDetails = (duplicateItem, selectedNewDeal) => {
    // Create a modified duplicate item with just the selected new deal
    const modifiedDuplicateItem = {
      ...duplicateItem,
      new_deal: selectedNewDeal,
      new_deals: undefined // Remove the array to match the modal's expected structure
    };
    setSelectedDuplicateDeal(modifiedDuplicateItem);
    setIsDuplicateModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedDeal(null);
  };

  const closeDuplicateModal = () => {
    setIsDuplicateModalOpen(false);
    setSelectedDuplicateDeal(null);
  };

  // Separate deals by type
  const duplicateDeals = pendingApprovals.filter(item => item.type === 'DUPLICATE');
  const kamApprovalDeals = pendingApprovals.filter(item => item.type === 'KAM_APPROVAL');
  const duplicateEnquiries = pendingApprovals.filter(item => item.type === 'ENQUIRY_DUPLICATE');

  // Flatten duplicate deals to show individual new deals
  const flattenedDuplicateDeals = duplicateDeals.reduce((acc, duplicateGroup) => {
    if (duplicateGroup.new_deals) {
      duplicateGroup.new_deals.forEach(newDeal => {
        acc.push({
          ...duplicateGroup,
          new_deal: newDeal,
          new_deals: undefined // Remove array for modal compatibility
        });
      });
    }
    return acc;
  }, []);

  // Check if user can approve duplicate deals
  const canApproveDuplicates = permissions?.duplicate_deals?.approve || false;

  const statsCards = [
    {
      title: "Total Pending",
      value: stats.total_pending,
      description: "Deals waiting for approval",
      icon: "⏳",
      color: "orange"
    },
    {
      title: "Total Approved",
      value: stats.total_approved,
      description: "Deals approved by you",
      icon: "✅",
      color: "green"
    },
    {
      title: "Total Rejected",
      value: stats.total_rejected,
      description: "Deals rejected by you",
      icon: "❌",
      color: "red"
    }
  ];

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-800">Error loading inbox: {error}</p>
            </div>
            <button 
              onClick={fetchData}
              className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-500">Nothing needs your attention here.</p>
        </div>
      </div>
    </div>
  );
};

export default Inbox;
